<script>
import { useVuelidate } from '@vuelidate/core';
import { email } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import parsePhoneNumber from 'libphonenumber-js/max';
import countries from 'shared/constants/countries.js';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import {
  normalizePhoneForInput,
  PHONE_VALIDATION_ERRORS,
  validatePhoneForInput,
} from 'shared/helpers/phoneNumberValidation';
import { mapGetters } from 'vuex';

export default {
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    inProgress: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    showRequiredFieldWarning: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      countries: countries,
      companyName: '',
      description: '',
      email: '',
      name: '',
      phoneNumber: '',
      activeDialCode: '',
      avatarFile: null,
      avatarUrl: '',
      assigneeId: null,
      teamId: null,
      country: {
        id: '',
        name: '',
      },
      city: '',
      socialProfileUserNames: {
        facebook: '',
        twitter: '',
        linkedin: '',
        github: '',
        instagram: '',
        line: '',
        telegram: '',
        website: '',
      },
      socialProfileKeys: [
        { key: 'facebook', label: 'https://facebook.com/' },
        { key: 'twitter', label: 'https://twitter.com/' },
        { key: 'linkedin', label: 'https://linkedin.com/' },
        { key: 'github', label: 'https://github.com/' },
        { key: 'instagram', label: 'https://instagram.com/' },
        { key: 'line', label: 'Line User Id ' },
        { key: 'telegram', label: 'Telegram Username ' },
        { key: 'website', label: 'Website: https://' },
      ],
      frozenContactId: null,
    };
  },
  validations: {
    name: {},
    description: {},
    email: {
      email,
    },
    companyName: {},
    phoneNumber: {},
    bio: {},
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
      myTeams: 'teams/getMyTeams',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      currentAccountId: 'getCurrentAccountId',
    }),
    currentUserId() {
      return Number(this.currentUser.id);
    },
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    normalizedContactAssigneeId() {
      return this.contact.assignee_id ? Number(this.contact.assignee_id) : null;
    },
    normalizedContactTeamId() {
      return this.contact.team_id ? Number(this.contact.team_id) : null;
    },
    currentTeamOption() {
      if (this.normalizedContactTeamId === null) return null;

      return (
        this.contact.team ||
        this.teams.find(
          team => Number(team.id) === this.normalizedContactTeamId
        ) ||
        null
      );
    },
    selectableTeams() {
      if (!this.currentTeamOption) {
        return this.teams;
      }

      const hasCurrentTeam = this.teams.some(
        team => Number(team.id) === Number(this.currentTeamOption.id)
      );

      return hasCurrentTeam
        ? this.teams
        : [this.currentTeamOption, ...this.teams];
    },
    canManageAllAssignments() {
      const isAdministrator = this.currentRole === 'administrator';
      const hasPermission = hasPermissions(
        ['contact_assignment_manage', 'contact_manage'],
        this.userPermissions
      );
      return isAdministrator || hasPermission;
    },
    isAssigneeDisabled() {
      // Se for criação (não tem id), não está desabilitado
      if (!this.contact.id) return false;

      const oldAssigneeId = this.normalizedContactAssigneeId;
      // Regra: Pode mudar se estiver vazio, se for admin/permissão especial, ou se for o dono atual
      const isUnassigned = !oldAssigneeId;
      const isCurrentOwner = oldAssigneeId === this.currentUserId;

      return !(isUnassigned || this.canManageAllAssignments || isCurrentOwner);
    },
    isTeamDisabled() {
      if (!this.contact.id) return false;

      const oldTeamId = this.normalizedContactTeamId;
      const isUnassigned = !oldTeamId;
      const isCurrentTeamMember = this.myTeams.some(
        team => Number(team.id) === oldTeamId
      );

      return !(
        isUnassigned ||
        this.canManageAllAssignments ||
        isCurrentTeamMember
      );
    },
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    isPhoneNumberNotValid() {
      if (!this.phoneNumber) return false;
      return !this.phoneValidation.isValid;
    },
    phoneNumberError() {
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.MISSING_DIAL_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.BRAZIL_AREA_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_TOO_SHORT
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_SHORT_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.PHONE_TOO_LONG
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_LONG_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_INVALID_LENGTH
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.INVALID_LENGTH_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.INVALID_PHONE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    normalizedPhoneNumber() {
      return normalizePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    phoneValidation() {
      return validatePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.normalizedPhoneNumber;
    },
    hasAtLeastOneRequiredField() {
      const name = this.name?.trim();
      const email = this.email?.trim();
      const phoneNumber = this.setPhoneNumber?.trim();
      const identifier = this.identifier?.trim();
      const hasSocialProfile = Object.values(this.socialProfileUserNames).some(
        v => v?.trim()
      );

      return Boolean(
        name || email || phoneNumber || identifier || hasSocialProfile
      );
    },
    shouldShowRequiredFieldWarning() {
      return (
        !this.hasAtLeastOneRequiredField &&
        (this.v$.$dirty || this.showRequiredFieldWarning)
      );
    },
  },
  watch: {
    contact() {
      // Freeze the contact when the editor opens so async store mutations
      // (websocket `contact.updated`, `conversation.updated`, `message.created`,
      // etc.) don't reset fields the user is typing. Only re-sync when the
      // contact id actually changes (i.e. user opens a different contact).
      const incomingId = this.contact && this.contact.id;
      if (!incomingId || incomingId === this.frozenContactId) return;
      this.frozenContactId = incomingId;
      this.setContactObject();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.frozenContactId = (this.contact && this.contact.id) || null;
    this.setContactObject();
    this.setDialCode();
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    countryNameWithCode({ name, id }) {
      if (!id) return name;
      if (!name && !id) return '';
      return `${name} (${id})`;
    },
    setDialCode() {
      if (
        this.phoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      }
    },
    setContactObject() {
      const {
        email: emailAddress,
        phone_number: phoneNumber,
        name,
      } = this.contact;
      const additionalAttributes = this.contact.additional_attributes || {};

      this.name = name || '';
      this.email = emailAddress || '';
      this.identifier = this.contact.identifier || '';
      this.phoneNumber = phoneNumber || '';
      this.companyName = additionalAttributes.company_name || '';
      this.country = {
        id:
          this.contact.country_code || additionalAttributes.country_code || '',
        name:
          additionalAttributes.country ||
          this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY'),
      };
      this.city = additionalAttributes.city || '';
      this.description = additionalAttributes.description || '';
      this.avatarUrl = this.contact.thumbnail || '';
      this.assigneeId = this.normalizedContactAssigneeId;
      this.teamId = this.normalizedContactTeamId;
      const {
        social_profiles: socialProfiles = {},
        screen_name: twitterScreenName,
      } = additionalAttributes;
      this.socialProfileUserNames = {
        twitter: socialProfiles.twitter || twitterScreenName || '',
        facebook: socialProfiles.facebook || '',
        linkedin: socialProfiles.linkedin || '',
        github: socialProfiles.github || '',
        instagram: socialProfiles.instagram || '',
        line: socialProfiles.line || '',
        telegram: socialProfiles.telegram || '',
        website: socialProfiles.website || '',
      };
    },
    getContactObject() {
      if (this.country === null) {
        this.country = {
          id: '',
          name: '',
        };
      }
      const contactObject = {
        id: this.contact.id,
        name: this.name,
        email: this.email,
        phone_number: this.setPhoneNumber,
        country_code: this.country.id,
        assignee_id: this.assigneeId,
        team_id: this.teamId,
        additional_attributes: {
          ...this.contact.additional_attributes,
          description: this.description,
          company_name: this.companyName,
          country_code: this.country.id,
          country:
            this.country.name ===
            this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')
              ? ''
              : this.country.name,
          city: this.city,
          social_profiles: this.socialProfileUserNames,
        },
      };
      if (this.avatarFile) {
        contactObject.avatar = this.avatarFile;
        contactObject.isFormData = true;
      }
      return contactObject;
    },
    onPhoneNumberInputChange(value, code) {
      this.activeDialCode = code;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    async handleSubmit() {
      this.v$.$touch();
      if (
        this.v$.$invalid ||
        this.isPhoneNumberNotValid ||
        !this.hasAtLeastOneRequiredField
      ) {
        return;
      }
      try {
        await this.onSubmit(this.getContactObject());
        this.onSuccess();
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          if (error.data.includes('email')) {
            useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
          } else if (error.data.includes('phone_number')) {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);
          useAlert(this.$t('CONTACT_FORM.DELETE_AVATAR.API.SUCCESS_MESSAGE'));
        }
        this.avatarFile = null;
        this.avatarUrl = '';
        this.activeDialCode = '';
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('CONTACT_FORM.DELETE_AVATAR.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form
    class="w-full px-8 pt-6 pb-8 contact--form"
    @submit.prevent="handleSubmit"
  >
    <div>
      <div class="w-full">
        <woot-avatar-uploader
          :label="$t('CONTACT_FORM.FORM.AVATAR.LABEL')"
          :src="avatarUrl"
          :username-avatar="name"
          :delete-avatar="!!avatarUrl"
          class="settings-item"
          @change="handleImageUpload"
          @onAvatarDelete="handleAvatarDelete"
        />
      </div>
    </div>
    <div>
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}
          <input
            v-model.trim="name"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
            @input="v$.name.$touch"
          />
        </label>

        <label :class="{ error: v$.email.$error }">
          {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
          <input
            v-model.trim="email"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
            @input="v$.email.$touch"
          />
          <span v-if="v$.email.$error" class="message">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
          </span>
        </label>
        <span
          v-if="shouldShowRequiredFieldWarning"
          class="block w-full mb-2.5 text-sm text-yellow-700 dark:text-yellow-400"
        >
          {{ $t('CONTACT_FORM.FORM.REQUIRED_CONTACT_FIELD') }}
        </span>
      </div>
    </div>
    <div class="w-full">
      <label :class="{ error: v$.description.$error }">
        {{ $t('CONTACT_FORM.FORM.OBSERVATIONS.LABEL') }}
        <textarea
          v-model.trim="description"
          type="text"
          :placeholder="$t('CONTACT_FORM.FORM.OBSERVATIONS.PLACEHOLDER')"
          @input="v$.description.$touch"
        />
      </label>
    </div>
    <div>
      <div class="w-full">
        <label
          :class="{
            error: isPhoneNumberNotValid,
          }"
        >
          {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
          <woot-phone-input
            v-model="phoneNumber"
            :value="phoneNumber"
            :dial-code="activeDialCode"
            :error="isPhoneNumberNotValid"
            :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
            @input="onPhoneNumberInputChange"
            @blur="v$.phoneNumber.$touch"
            @setCode="setPhoneCode"
          />
          <span v-if="isPhoneNumberNotValid" class="message">
            {{ phoneNumberError }}
          </span>
        </label>
        <div
          v-if="isPhoneNumberNotValid || !phoneNumber"
          class="relative mx-0 mt-0 mb-2.5 p-2 rounded-md text-sm border border-solid border-yellow-500 text-yellow-700 dark:border-yellow-700 bg-yellow-200/60 dark:bg-yellow-200/20 dark:text-yellow-400"
        >
          {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.HELP') }}
        </div>
      </div>
    </div>
    <woot-input
      v-model.trim="companyName"
      class="w-full"
      :label="$t('CONTACT_FORM.FORM.COMPANY_NAME.LABEL')"
      :placeholder="$t('CONTACT_FORM.FORM.COMPANY_NAME.PLACEHOLDER')"
    />
    <div class="w-full">
      <label :class="{ error: isAssigneeDisabled }">
        {{ $t('CONTACT_FORM.FORM.ASSIGNEE.LABEL') }}
        <select
          v-model="assigneeId"
          :disabled="isAssigneeDisabled"
          class="w-full mb-0"
        >
          <option :value="null">
            {{ $t('CONTACT_FORM.FORM.ASSIGNEE.PLACEHOLDER') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <span v-if="isAssigneeDisabled" class="message">
          {{ $t('CONTACT_FORM.FORM.ASSIGNEE.HELP') }}
        </span>
      </label>
    </div>
    <div class="w-full">
      <label :class="{ error: isTeamDisabled }">
        {{ $t('CONTACT_FORM.FORM.TEAM.LABEL') }}
        <select v-model="teamId" :disabled="isTeamDisabled" class="w-full mb-0">
          <option :value="null">
            {{ $t('CONTACT_FORM.FORM.TEAM.PLACEHOLDER') }}
          </option>
          <option
            v-for="team in selectableTeams"
            :key="team.id"
            :value="team.id"
          >
            {{ team.name }}
          </option>
        </select>
        <span v-if="isTeamDisabled" class="message">
          {{ $t('CONTACT_FORM.FORM.TEAM.HELP') }}
        </span>
      </label>
    </div>
    <div>
      <div class="w-full">
        <label>
          {{ $t('CONTACT_FORM.FORM.COUNTRY.LABEL') }}
        </label>
        <multiselect
          v-model="country"
          track-by="id"
          label="name"
          :placeholder="$t('CONTACT_FORM.FORM.COUNTRY.PLACEHOLDER')"
          selected-label
          :select-label="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_PLACEHOLDER')"
          :deselect-label="$t('CONTACT_FORM.FORM.COUNTRY.REMOVE')"
          :custom-label="countryNameWithCode"
          :max-height="160"
          :options="countries"
          allow-empty
          :option-height="104"
        />
      </div>
    </div>
    <woot-input
      v-model="city"
      class="w-full"
      :label="$t('CONTACT_FORM.FORM.CITY.LABEL')"
      :placeholder="$t('CONTACT_FORM.FORM.CITY.PLACEHOLDER')"
    />

    <div class="w-full">
      <label>{{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.SOCIAL_PROFILES') }}</label>
      <div
        v-for="socialProfile in socialProfileKeys"
        :key="socialProfile.key"
        class="flex items-stretch w-full mb-4"
      >
        <span
          class="flex items-center h-10 px-2 text-sm border-solid bg-surface-hover border-y ltr:border-l rtl:border-r ltr:rounded-l-md rtl:rounded-r-md text-body border-line"
          >{{ socialProfile.label }}</span
        >
        <input
          v-model="socialProfileUserNames[socialProfile.key]"
          class="input-group-field ltr:!rounded-l-none rtl:rounded-r-none !mb-0"
          type="text"
        />
      </div>
    </div>
    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <div class="w-full">
        <woot-submit-button
          :loading="inProgress"
          :button-text="$t('CONTACT_FORM.FORM.SUBMIT')"
        />
        <button class="button clear" @click.prevent="onCancel">
          {{ $t('CONTACT_FORM.FORM.CANCEL') }}
        </button>
      </div>
    </div>
  </form>
</template>

<style scoped lang="scss">
::v-deep {
  .multiselect .multiselect__tags .multiselect__single {
    @apply pl-0;
  }
}
</style>
